"use client";

import { useState, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, CircleCheck as CheckCircle2, Phone, Sparkles, MapPin, Search, ChevronRight, ShieldCheck, UserCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const CATEGORIES = ["All Questions", "Strategy & Results", "Google Maps & GBP", "Services & Pricing"] as const;
type CategoryType = (typeof CATEGORIES)[number];
interface FaqItem {
  id: string;
  category: CategoryType;
  question: string;
  answer: string;
  highlight?: string;
}
const FAQ_DATA: FaqItem[] = [{
  id: "faq-1",
  category: "Strategy & Results",
  question: "Why should my Dundee business hire an independent SEO consultant rather than an agency?",
  answer: "When you hire an agency, your account is frequently handed down to junior account executives juggling dozens of clients. When you work with me, you gain direct access to my hands-on SEO expertise. Every technical audit, keyword strategy, localized content blueprint, and link authority campaign is executed by me personally—ensuring strategic precision, rapid implementation, and direct accountability.",
  highlight: "Direct direct senior consultant access with zero agency middleman overhead."
}, {
  id: "faq-2",
  category: "Strategy & Results",
  question: "How long does it take to see first-page Google rankings in Dundee and Tayside?",
  answer: "Local SEO results usually begin demonstrating organic growth within 3 to 6 months depending on competition in Dundee and your website's baseline technical health. Google Business Profile (Maps) optimizations often yield faster local visibility gains within 30 to 90 days. My focus is on targeting high-intent local queries that convert into calls and enquiries while building sustainable long-term authority.",
  highlight: "Map Pack wins in 30-90 days; scalable organic growth within 3-6 months."
}, {
  id: "faq-3",
  category: "Google Maps & GBP",
  question: "What makes Dundee local SEO distinct from general UK national SEO?",
  answer: "Dundee local SEO captures hyper-targeted buyer intent within specific geographic bounds (e.g., 'commercial electrician Dundee' or 'solicitor Broughty Ferry'). It heavily relies on Google Business Profile optimization, localized schema markups, citations across Scottish directories, and geo-focused content that signals strong proximity and local relevance to Google's ranking algorithms.",
  highlight: "Hyper-targeted geographic signals designed specifically for the Dundee & Tayside catchment area."
}, {
  id: "faq-4",
  category: "Google Maps & GBP",
  question: "Will you fully manage and optimize my Dundee Google Business Profile?",
  answer: "Yes. Google Business Profile optimization is a core component of my local SEO service. I audit primary business categories, refine attribute data, set up localized image assets, clean up NAP (Name, Address, Phone) consistency, manage local citations, and help you deploy effective customer review acquisition workflows.",
  highlight: "Complete GBP optimization to maximize 3-Pack Map presence."
}, {
  id: "faq-5",
  category: "Strategy & Results",
  question: "Can your local SEO strategies cover Dundee, Angus, Fife, and Perthshire?",
  answer: "Absolutely. Many Dundee-based enterprises serve regional markets across the Tay Cities area. I build structured multi-location landing page architectures that naturally rank for nearby hubs like St Andrews, Perth, Arbroath, and Forfar without triggering search engine duplication filters.",
  highlight: "Scalable multi-town landing page strategies across Tayside and Fife."
}, {
  id: "faq-6",
  category: "Services & Pricing",
  question: "What is your process when starting a local SEO project with a Dundee client?",
  answer: "We begin with a thorough Local SEO & Technical Audit to uncover ranking bottlenecks, map listing errors, and competitor gaps. Next, I draft a customized 6-month roadmap prioritizing quick wins and strategic gains. Throughout execution, you receive direct monthly video walkthroughs and live reporting dashboards detailing keyword rankings, Google Map impressions, and organic conversions.",
  highlight: "Full transparent roadmap with monthly video reviews and clear deliverables."
}, {
  id: "faq-7",
  category: "Strategy & Results",
  question: "Do you offer AI Search Optimisation (AIO) for Dundee searches?",
  answer: "Yes. Search is evolving rapidly with AI-driven answer engines like Google AI Overviews, ChatGPT, and Perplexity. I structure your site's entity data, schema, and local authority so AI search tools confidently feature your Dundee business as the authoritative recommendation.",
  highlight: "Future-proof visibility across both traditional search and AI engine overviews."
}, {
  id: "faq-8",
  category: "Services & Pricing",
  question: "How much does local SEO consultancy cost for a Dundee business?",
  answer: "My Dundee local SEO consultancy operates on tailored monthly retainers or fixed-scope project fees, scaled to your sector's competitiveness and growth goals. Operating as an independent specialist without glossy city office overheads means 100% of your investment directly drives campaign performance and technical execution.",
  highlight: "Transparent retainer structures with zero agency bloat or locked-in hidden fees."
}, {
  id: "faq-9",
  category: "Google Maps & GBP",
  question: "How do you build authentic local links and citations for Dundee businesses?",
  answer: "I earn genuine, high-quality links and citations across Dundee, Tayside, and Scotland—such as verified Scottish business registers, regional industry bodies, local news features, and community sponsorships. I strictly avoid automated spam links that risk Google penalties.",
  highlight: "100% white-hat local links and legitimate Scottish citations."
}, {
  id: "faq-10",
  category: "Services & Pricing",
  question: "Will I work directly with you, Yassine Essaadi, or be assigned a team member?",
  answer: "You work 100% directly with me, Yassine Essaadi. From our strategic discovery session to technical fixes, content briefs, and monthly review calls, I am your sole consultant. You will never be pushed to an account manager or junior junior trainee.",
  highlight: "Guaranteed 1-on-1 direct partnership with Yassine Essaadi."
}, {
  id: "faq-11",
  category: "Strategy & Results",
  question: "What local industries in Dundee do you have experience working with?",
  answer: "I work with a diverse spectrum of businesses across Dundee, including B2B professional services, legal practices, healthcare clinics, local trade services, e-commerce brands, and tech providers. The fundamentals of search intent, technical performance, and localized authority apply universally across industries.",
  highlight: "Versatile SEO strategies customized for Dundee professional services, trades, and local retail."
}, {
  id: "faq-12",
  category: "Services & Pricing",
  question: "How do we get started with your Dundee local SEO consultancy?",
  answer: "Getting started is straightforward. Click 'Book a Free SEO Consultation' to schedule a direct 30-minute consultation with me. I will review your current Dundee search presence, evaluate your top local competitors, and outline actionable steps before you commit to anything.",
  highlight: "Free 30-minute discovery call with actionable insights before signing."
}];
export default function DundeeFaq() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryType>("All Questions");
  const [searchQuery, setSearchQuery] = useState("");
  const shouldReduceMotion = useReducedMotion();
  const filteredFaqs = useMemo(() => {
    return FAQ_DATA.filter(item => {
      const matchesCategory = selectedCategory === "All Questions" || item.category === selectedCategory;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);
  return <section id="dundeelocalseofaq" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 blur-3xl pointer-events-none -z-10 rounded-full opacity-50" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 blur-3xl pointer-events-none -z-10 rounded-full" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary bg-primary/5 rounded-full text-xs font-semibold tracking-wide uppercase">
            <MapPin className="size-3.5 mr-1 text-primary" />
            Dundee Local SEO Expertise
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-6 leading-tight">
            Frequently Asked Questions About{" "}
            <span className="text-primary underline decoration-accent/40 decoration-wavy underline-offset-8">
              Dundee Local SEO
            </span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers directly from <strong className="text-foreground font-semibold">Yassine Essaadi</strong>.
            Discover how working directly with an independent SEO consultant delivers direct results for your Dundee and Tayside business.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <UserCheck className="size-4 text-primary" /> Direct Consultant Access
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <ShieldCheck className="size-4 text-primary" /> No Agency Middlemen
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Sparkles className="size-4 text-primary" /> Transparent Pricing
            </span>
          </div>
        </div>

        {}
        <div className="mb-10 space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card p-4 rounded-xl border border-border shadow-xs">
            {}
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {CATEGORIES.map((cat, i) => {
              const isActive = selectedCategory === cat;
              return <Button key={cat} data-index={i} variant={isActive ? "default" : "ghost"} size="sm" onClick={() => setSelectedCategory(cat)} className={`rounded-lg transition-all duration-200 text-xs sm:text-sm ${isActive ? "bg-primary text-primary-foreground shadow-xs font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
                    {cat}
                  </Button>;
            })}
            </div>

            {}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search Dundee SEO questions..." className="w-full pl-9 pr-4 py-2 bg-background text-foreground border border-input rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors" />
            </div>
          </div>
        </div>

        {}
        {filteredFaqs.length > 0 ? <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="space-y-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {filteredFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="bg-card border border-border rounded-xl px-4 sm:px-6 py-1 shadow-2xs hover:shadow-xs hover:border-primary/30 transition-[box-shadow,border-color] duration-200">
                  <AccordionTrigger className="hover:no-underline py-4 text-left group">
                    <div className="flex items-start gap-3 sm:gap-4 pr-2">
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        {i + 1}
                      </span>
                      <span className="text-base sm:text-lg font-semibold text-primary font-default leading-snug group-hover:text-primary/90 transition-colors">
                        {faq.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-5 text-muted-foreground leading-relaxed text-sm sm:text-base border-t border-border/50 mt-2">
                    <p className="mb-4">{faq.answer}</p>
                    {faq.highlight && <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50 border border-secondary text-foreground text-xs sm:text-sm">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="font-medium">{faq.highlight}</span>
                      </div>}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div> : <div className="text-center py-12 bg-card rounded-xl border border-border">
            <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3 opacity-60" />
            <h3 className="text-lg font-semibold text-foreground mb-1">No matching questions found</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Try adjusting your search term or select another category filter.
            </p>
            <Button variant="outline" size="sm" onClick={() => {
          setSelectedCategory("All Questions");
          setSearchQuery("");
        }}>
              Reset Filters
            </Button>
          </div>}

        <Separator className="my-12 lg:my-16" />

        {}
        <div className="relative rounded-2xl bg-card border border-border p-6 sm:p-8 lg:p-10 shadow-md overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            {}
            <div className="lg:col-span-8 space-y-4 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <MessageSquare className="size-3.5" />
                <span>Have a specific question about your Dundee site?</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
                Speak directly with Yassine Essaadi
              </h3>

              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-2xl">
                No sales teams, no automated proposals. Get honest, actionable advice about your local search potential in Dundee and Tayside.
              </p>

              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-4 text-xs sm:text-sm text-muted-foreground">
                <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors font-medium">
                  <MessageSquare className="size-4 text-primary" />
                  contact@ye-consultancy.com
                </a>
                <span className="hidden sm:inline text-border">•</span>
                <a href="tel:+447490984430" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors font-medium">
                  <Phone className="size-4 text-primary" />
                  +44 7490984430
                </a>
              </div>
            </div>

            {}
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
              <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-sm transition-transform active:scale-[0.98]">
                <Link to="/ContactPage">
                  <span>Book Free SEO Call</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full border-border text-foreground hover:bg-muted font-medium">
                <Link to="/CaseStudiesPage">
                  <span>Explore Dundee Case Studies</span>
                  <ChevronRight className="size-4 ml-1 text-muted-foreground" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}